import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { createClientInquiry, clearError, fetchPrograms, checkPatientByCnic } from "../../store/slices/inquirySlice";

import MainLayout from "../../components/layout/MainLayout";
import EnquirerInfo from "../Patient/manage/EnquiryForm/EnquirerInfo";
import ClientInfo from "../Patient/manage/EnquiryForm/ClientInfo";
import Medications from "../Patient/manage/EnquiryForm/Medications";
import PhysicalHealth from "../Patient/manage/EnquiryForm/PhysicalHealth";
import RiskAssessment from "../Patient/manage/EnquiryForm/RiskAssessment";
import AggressionHistory from "../Patient/manage/EnquiryForm/AggressionHistory";
import ClientBehavior from "../Patient/manage/EnquiryForm/ClientBehavior";
import TreatmentPlan from "../Patient/manage/EnquiryForm/TreatmentPlan";


const AddInquiry = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isLoading, error } = useSelector((state) => state.inquiries);
    const { programs, isProgramsLoading } = useSelector((state) => state.inquiries);

    const [activeTab, setActiveTab] = useState("enquirer-info");
    const [selectedProgram, setSelectedProgram] = useState("1");

    useEffect(() => {
        dispatch(fetchPrograms());
    }, [dispatch]);

    const [formData, setFormData] = useState({
        program_master_id: selectedProgram,
        contactMethod: "",
        inquirerName: "",
        relationWithClient: "",
        phone1: "",
        phone2: "",
        email: "",
        address: "",
        howHeard: [],
        referredBy: { psychiatrist: "", psychologist: "", otherMedical: "" },
        otherSource: "",
        clientName: "",
        clientGender: "",
        clientDOB: "",
        client_cnic: "",
        client_phone: "",
        client_email: "",
        client_address: "",
        clientEducation: "",
        clientBirthOrder: "",
        clientSiblings: "",
        familyMentalIllness: "",
        familyMentalIllnessDetails: "",
        diagnosis: "",
        diagnosisSince: "",
        presentingComplaints: "",
        currentCare: "",
        trhServices: "",
        trhServicesDetails: "",
        substanceUse: "",
        substanceUseDetails: "",
        medications: [{ name: "", dosage: "" }],
        prescribedBy: "",
        prescriptionDate: "",
        prevServices: [],
        otherService: "",
        prevHospitalizations: [{ hospital: "", duration: "", consultant: "" }],
        medicalConditions: "",
        medicalConditionsDetails: "",
        healthIssues: [],
        otherHealthIssue: "",
        illnessHistory: "",
        expectations: "",
        suicidalThoughts: "",
        suicideFrequency: "",
        suicidePlans: "",
        suicideAttempts: "",
        suicideAttemptsDetails: "",
        desireEscape: "",
        escapeAttempts: "",
        escapeAttemptsDetails: "",
        historyAggression: "",
        historyAggressionDetails: "",
        harmToOthers: "",
        harmToOthersDetails: "",
        selfHarmHistory: "",
        selfHarmHistoryDetails: "",
        selfHarmUrges: "",
        selfHarmUrgesDetails: "",
        recommendedFor: [],
        opdDetails: "",
        followUpNotes: "",
        prev_aggression: "",
        last_episode: "",
        incident_provoked: "",
        regret_incident: "",
        show_denial: "",
        episode_description: "",
        cope_stress: "",
        client_general_behavior: "",
        impulsive_behavior: "",
        impulsive_example_1: "",
        impulsive_example_2: "",
        impulsive_example_3: "",
        symptoms: "",
        rehab_expectations: "",
        fear_aggression: "",
        psychiatrist_note: "",
        treatmentOption: "",
        reason: "",
        amount: "",
        payment_status: "",
        reference_no: "",
        payment_date: "",
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(clearError());
        return () => dispatch(clearError());
    }, [dispatch]);

    // ================= MASKING HELPERS =================

    // CNIC: xxxxx-xxxxxxx-x
    const maskCNIC = (value) => {
        const digits = value.replace(/\D/g, "").slice(0, 13);

        if (digits.length <= 5) return digits;
        if (digits.length <= 12) {
            return `${digits.slice(0, 5)}-${digits.slice(5)}`;
        }
        return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12, 13)}`;
    };

    // Phone: xxxx-xxxxxxx
    const maskPhone = (value) => {
        const digits = value.replace(/\D/g, "").slice(0, 11);

        if (digits.length <= 4) return digits;
        return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    };


    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        let finalValue = value;

        // ===== APPLY MASKING =====
        if (name === "client_cnic") {
            finalValue = maskCNIC(value);
        }

        if (name === "client_phone" || name === "phone1" || name === "phone2") {
            finalValue = maskPhone(value);
        }

        // ===== INLINE VALIDATION (EMAIL) =====
        if (name === "client_email" || name === "email") {
            let errorMsg = "";
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            if (value && !value.includes("@")) {
                errorMsg = "Email must contain '@'";
            } else if (value && !emailRegex.test(value)) {
                errorMsg = "Invalid email format";
            }

            setErrors((prev) => ({
                ...prev,
                [name]: errorMsg,
            }));
        }

        // Clear other field errors when user starts typing
        if (errors[name] && name !== "client_email" && name !== "email") {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }

        // ===== CHECKBOX HANDLING =====
        if (type === "checkbox") {
            const updatedArray = checked
                ? [...formData[name], value]
                : formData[name].filter((item) => item !== value);

            setFormData({ ...formData, [name]: updatedArray });
            return;
        }

        // ===== REFERRED BY =====
        if (name.includes("referredBy")) {
            const [, subKey] = name.split(".");
            setFormData({
                ...formData,
                referredBy: { ...formData.referredBy, [subKey]: finalValue },
            });
            return;
        }

        // ===== ARRAY FIELDS =====
        if (name.includes("medications") || name.includes("prevHospitalizations")) {
            const [field, index, subField] = name.split(".");
            const updatedArray = [...formData[field]];
            updatedArray[index] = {
                ...updatedArray[index],
                [subField]: finalValue,
            };
            setFormData({ ...formData, [field]: updatedArray });
            return;
        }

        // ===== DEFAULT =====
        setFormData({
            ...formData,
            [name]: finalValue,
        });
    };

    // ================= TAB VALIDATIONS =================

    const validateEnquirerInfo = () => {
        const newErrors = {};

        if (!formData.contactMethod) {
            newErrors.contactMethod = "Contact method is required";
        }

        if (!formData.inquirerName?.trim()) {
            newErrors.inquirerName = "Inquirer name is required";
        }

        if (!formData.phone1?.trim()) {
            newErrors.phone1 = "Phone number is required";
        }

        if (!formData.email?.trim()) {
            newErrors.email = "Email is required";
        }

        if (!formData.relationWithClient) {
            newErrors.relationWithClient = "Relation with client is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ===== FORMAT VALIDATIONS (email, phone, cnic, name) =====
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const validateEnquirerFormats = () => {
        const e = {};
        if (formData.inquirerName && formData.inquirerName.trim().length < 2) {
            e.inquirerName = "Name is too short";
        }
        if (formData.phone1 && formData.phone1.replace(/\D/g, "").length < 7) {
            e.phone1 = "Invalid phone number";
        }
        if (formData.email && !emailRegex.test(formData.email)) {
            e.email = "Invalid email address";
        }
        setErrors((prev) => ({ ...prev, ...e }));
        return Object.keys(e).length === 0;
    };

    const validateClientInfo = () => {
        const newErrors = {};

        if (!formData.clientName?.trim()) {
            newErrors.clientName = "Client name is required";
        }

        if (!formData.clientGender?.trim()) {
            newErrors.clientGender = "Gender is required";
        }

        if (!formData.clientDOB?.trim()) {
            newErrors.clientDOB = "Date of birth is required";
        }

        if (!formData.client_phone?.trim()) {
            newErrors.client_phone = "Phone number is required";
        }

        if (!formData.client_email?.trim()) {
            newErrors.client_email = "Email is required";
        }

        if (!formData.client_address?.trim()) {
            newErrors.client_address = "Address is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateClientFormats = () => {
        const e = {};
        if (formData.clientName && formData.clientName.trim().length < 2) {
            e.clientName = "Name is too short";
        }
        if (formData.client_phone && formData.client_phone.replace(/\D/g, "").length < 7) {
            e.client_phone = "Invalid phone number";
        }
        if (formData.client_email && !emailRegex.test(formData.client_email)) {
            e.client_email = "Invalid email address";
        }
        const cnicDigits = (formData.client_cnic || "").replace(/\D/g, "");
        if (formData.client_cnic && formData.client_cnic.trim() && cnicDigits.length !== 13) {
            e.client_cnic = "Invalid CNIC format";
        }
        setErrors((prev) => ({ ...prev, ...e }));
        return Object.keys(e).length === 0;
    };

    const validateMedications = () => {
        const newErrors = {};

        // at least one medication name
        if (!formData.medications?.[0]?.name?.trim()) {
            newErrors.medications = "At least one medication is required";
        }

        if (!formData.prescribedBy?.trim()) {
            newErrors.prescribedBy = "Prescribed by is required";
        }

        if (!formData.prescriptionDate) {
            newErrors.prescriptionDate = "Prescription date is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validatePhysicalHealth = () => {
        const newErrors = {};

        if (!formData.medicalConditions) {
            newErrors.medicalConditions = "Please specify if there are known medical conditions";
        }

        if (formData.medicalConditions === "Yes" && !formData.medicalConditionsDetails?.trim()) {
            newErrors.medicalConditionsDetails = "Please specify the medical conditions";
        }

        if (!formData.illnessHistory?.trim()) {
            newErrors.illnessHistory = "Brief history of illness is required";
        }

        if (!formData.expectations?.trim()) {
            newErrors.expectations = "Expectations from rehabilitation services is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateRiskAssessment = () => {
        const newErrors = {};

        // Section A: Suicide Risk
        if (!formData.suicidalThoughts) {
            newErrors.suicidalThoughts = "Please specify if there are suicidal thoughts";
        }

        if (formData.suicidalThoughts === "Yes" && !formData.suicideFrequency) {
            newErrors.suicideFrequency = "Please specify frequency of suicidal thoughts";
        }

        if (!formData.suicidePlans) {
            newErrors.suicidePlans = "Please specify if there are current or past suicide plans";
        }

        if (!formData.suicideAttempts) {
            newErrors.suicideAttempts = "Please specify if there were previous suicide attempts";
        }

        if (formData.suicideAttempts === "Yes" && !formData.suicideAttemptsDetails?.trim()) {
            newErrors.suicideAttemptsDetails = "Please provide details of previous suicide attempts";
        }

        // Section B: Escape Risk
        if (!formData.desireEscape) {
            newErrors.desireEscape = "Please specify if there is a desire to escape";
        }

        if (!formData.escapeAttempts) {
            newErrors.escapeAttempts = "Please specify if there were past escape attempts";
        }

        if (formData.escapeAttempts === "Yes" && !formData.escapeAttemptsDetails?.trim()) {
            newErrors.escapeAttemptsDetails = "Please provide details of past escape attempts";
        }

        // Section C: Aggression Risk
        if (!formData.historyAggression) {
            newErrors.historyAggression = "Please specify if there is a history of aggression";
        }

        if (formData.historyAggression === "Yes" && !formData.historyAggressionDetails?.trim()) {
            newErrors.historyAggressionDetails = "Please provide details of history of aggression";
        }

        if (!formData.harmToOthers) {
            newErrors.harmToOthers = "Please specify if there is a current risk of harm to others";
        }

        if (formData.harmToOthers === "Yes" && !formData.harmToOthersDetails?.trim()) {
            newErrors.harmToOthersDetails = "Please specify any specific individuals at risk";
        }

        // Section D: Self-Harm Risk
        if (!formData.selfHarmHistory) {
            newErrors.selfHarmHistory = "Please specify if there is a history of self-harm";
        }

        if (formData.selfHarmHistory === "Yes" && !formData.selfHarmHistoryDetails?.trim()) {
            newErrors.selfHarmHistoryDetails = "Please provide details of history of self-harm";
        }

        if (!formData.selfHarmUrges) {
            newErrors.selfHarmUrges = "Please specify if there are current self-harm urges";
        }

        if (formData.selfHarmUrges === "Yes" && !formData.selfHarmUrgesDetails?.trim()) {
            newErrors.selfHarmUrgesDetails = "Please specify specific actions considered";
        }

        // Recommended For
        if (formData.recommendedFor.length === 0) {
            newErrors.recommendedFor = "Please select at least one recommendation";
        }

        if (formData.recommendedFor.includes("OPD Services outside TRH") && !formData.opdDetails?.trim()) {
            newErrors.opdDetails = "Please provide details for OPD services";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateAggressionHistory = () => {
        const newErrors = {};

        if (!formData.prev_aggression) {
            newErrors.prev_aggression = "Please specify if there was a previous episode of aggression/violence";
        }

        if (formData.prev_aggression === "Yes") {
            if (!formData.last_episode?.trim()) {
                newErrors.last_episode = "Please specify when was the last episode";
            }

            if (!formData.incident_provoked) {
                newErrors.incident_provoked = "Please specify if the incident was provoked";
            }

            if (!formData.regret_incident) {
                newErrors.regret_incident = "Please specify if he/she regrets the incident";
            }

            if (!formData.show_denial) {
                newErrors.show_denial = "Please specify if he/she shows denial";
            }

            if (!formData.episode_description?.trim()) {
                newErrors.episode_description = "Please describe the episode briefly";
            }
        }

        if (!formData.cope_stress?.trim()) {
            newErrors.cope_stress = "Please describe how the client handles/copes with stress";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateClientBehavior = () => {
        const newErrors = {};

        if (!formData.client_general_behavior) {
            newErrors.client_general_behavior = "Please select the client's general behavior";
        }

        if (!formData.impulsive_behavior) {
            newErrors.impulsive_behavior = "Please specify if the client exhibits impulsive behavior";
        }

        if (formData.impulsive_behavior === "Yes") {
            if (!formData.impulsive_example_1?.trim()) {
                newErrors.impulsive_example_1 = "Please provide first example of impulsive behavior";
            }
            if (!formData.impulsive_example_2?.trim()) {
                newErrors.impulsive_example_2 = "Please provide second example of impulsive behavior";
            }
            if (!formData.impulsive_example_3?.trim()) {
                newErrors.impulsive_example_3 = "Please provide third example of impulsive behavior";
            }
        }

        if (!formData.symptoms?.trim()) {
            newErrors.symptoms = "Please describe the symptoms";
        }

        if (!formData.rehab_expectations?.trim()) {
            newErrors.rehab_expectations = "Please describe expectations from rehabilitation services";
        }

        if (!formData.fear_aggression) {
            newErrors.fear_aggression = "Please specify if you fear another episode of violence/aggression";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateTreatmentPlan = () => {
        const newErrors = {};

        if (!formData.treatmentOption) {
            newErrors.treatmentOption = "Please select a treatment plan";
        }

        if (!formData.reason?.trim()) {
            newErrors.reason = "Please provide a reason for the selected treatment plan";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEnquirerNext = () => {
        if (!validateEnquirerInfo()) return;
        if (!validateEnquirerFormats()) return;
        setActiveTab("client-info");
    };

    const handleClientNext = () => {
        if (!validateClientInfo()) return;
        if (!validateClientFormats()) return;
        setActiveTab("medications");
    };

    const handleMedicationsNext = () => {
        if (!validateMedications()) return;

        if (selectedProgram === "2") {
            setActiveTab("aggression-history");
        } else {
            setActiveTab("physical-health");
        }
    };

    const handlePhysicalHealthNext = () => {
        if (validatePhysicalHealth()) {
            setActiveTab("risk-assessment");
        }
    };

    const handleAggressionHistoryNext = () => {
        if (validateAggressionHistory()) {
            setActiveTab("client-behavior");
        }
    };

    const handleClientBehaviorNext = () => {
        if (validateClientBehavior()) {
            setActiveTab("treatment-plan");
        }
    };

    const addMedication = () => {
        setFormData({
            ...formData,
            medications: [...formData.medications, { name: "", dosage: "" }],
        });
    };

    const removeMedication = (index) => {
        const updatedMeds = formData.medications.filter((_, i) => i !== index);
        setFormData({ ...formData, medications: updatedMeds });
    };

    const addHospitalization = () => {
        setFormData({
            ...formData,
            prevHospitalizations: [
                ...formData.prevHospitalizations,
                { hospital: "", duration: "", consultant: "" },
            ],
        });
    };

    const removeHospitalization = (index) => {
        const updatedHosp = formData.prevHospitalizations.filter((_, i) => i !== index);
        setFormData({ ...formData, prevHospitalizations: updatedHosp });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.contactMethod) newErrors.contactMethod = "Contact method is required";
        if (!formData.inquirerName) newErrors.inquirerName = "Inquirer name is required";
        if (!formData.clientName) newErrors.clientName = "Client name is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate based on selected program
        let isValid = false;

        if (selectedProgram === "2") {
            // Validate Treatment Plan for program 2
            isValid = validateTreatmentPlan();
        } else {
            // Validate Risk Assessment for programs 1 and 3
            isValid = validateRiskAssessment();
        }

        if (!isValid) {
            Swal.fire({
                title: "Incomplete Form",
                text: "Please fill in all required fields.",
                icon: "warning",
                confirmButtonText: "OK",
            });
            return;
        }

        // Check if CNIC belongs to an existing patient
        if (formData.client_cnic && formData.client_cnic.trim()) {
            try {
                const cnicRes = await dispatch(checkPatientByCnic(formData.client_cnic.trim())).unwrap();
                if (cnicRes?.exists && cnicRes?.patient) {
                    const patient = cnicRes.patient;
                    const patientName = patient.name || `${patient.first_name || ""} ${patient.last_name || ""}`.trim() || "Existing Patient";
                    const mrNo = patient.mr_no && patient.mr_no !== "N/A" ? ` (${patient.mr_no})` : "";

                    const confirmResult = await Swal.fire({
                        title: "Existing Patient Found",
                        html: `
                            <div class="text-start">
                                <p>The CNIC <strong>${formData.client_cnic}</strong> belongs to registered patient <strong>${patientName}${mrNo}</strong>.</p>
                                <p class="mb-0">Are you sure you want to create this inquiry? This inquiry will be <strong>directly linked to ${patientName}'s profile</strong>.</p>
                            </div>
                        `,
                        icon: "question",
                        showCancelButton: true,
                        confirmButtonColor: "#3085d6",
                        cancelButtonColor: "#d33",
                        confirmButtonText: "Yes, Create & Link",
                        cancelButtonText: "No, Change CNIC",
                        reverseButtons: true,
                        allowOutsideClick: false,
                    });

                    if (!confirmResult.isConfirmed) {
                        // Keep form data and switch to Client Info tab so the user can change the CNIC
                        setActiveTab("client-info");
                        return;
                    }
                }
            } catch (err) {
                console.error("Error checking patient CNIC:", err);
            }
        }

        const payload = {
            ...formData,
            suicidal_thoughts: formData.suicidalThoughts,
            frequency_suicidal_thoughts: formData.suicideFrequency,
            current_or_past_suicide_plans: formData.suicidePlans,
            previous_suicide_attempts: formData.suicideAttempts,
            previous_suicide_attempts_details: formData.suicideAttemptsDetails,

            desire_to_escape: formData.desireEscape,
            past_escape_attempts: formData.escapeAttempts,
            past_escape_attempts_details: formData.escapeAttemptsDetails,

            history_of_aggression: formData.historyAggression,
            history_of_aggression_details: formData.historyAggressionDetails,
            current_risk_of_harm_to_others: formData.harmToOthers,
            specific_individuals: formData.harmToOthersDetails,

            history_of_self_harm: formData.selfHarmHistory,
            history_of_self_harm_details: formData.selfHarmHistoryDetails,
            current_self_harm_urges: formData.selfHarmUrges,
            specific_self_harm_actions: formData.selfHarmUrgesDetails,

            treatment_option: formData.treatmentOption,
        };
        try {
            await dispatch(createClientInquiry(payload)).unwrap();
            await Swal.fire({
                title: "Success",
                text: "Inquiry submitted successfully!",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });
            navigate("/inquiries");
        } catch (error) {
            console.error("Failed to submit inquiry:", error);
            Swal.fire({
                title: "Error",
                text: `Failed to submit inquiry: ${error.message || JSON.stringify(error)}`,
                icon: "error",
            });
        }
    };

    // Tabs based on program
    const getTabs = () => {
        if (selectedProgram === "1" || selectedProgram === "3") {
            return [
                { key: "enquirer-info", icon: "ri-contacts-line", title: "Enquirer Info" },
                { key: "client-info", icon: "ri-user-line", title: "Client Info" },
                { key: "medications", icon: "ri-medicine-bottle-line", title: "Medications" },
                { key: "physical-health", icon: "ri-heart-line", title: "Physical Health" },
                { key: "risk-assessment", icon: "ri-shield-line", title: "Risk Assessment" },
            ];
        }

        return [
            { key: "enquirer-info", icon: "ri-contacts-line", title: "Enquirer Info" },
            { key: "client-info", icon: "ri-user-line", title: "Client Info" },
            { key: "medications", icon: "ri-medicine-bottle-line", title: "Medications" },
            { key: "aggression-history", icon: "ri-emotion-line", title: "Aggression History" },
            { key: "client-behavior", icon: "ri-psychotherapy-line", title: "Client Behavior" },
            { key: "treatment-plan", icon: "ri-file-list-3-line", title: "Treatment Plan" },
        ];
    };


    const tabs = getTabs();

    // Reset form to initial empty state
    const resetFormData = (programId = selectedProgram) => ({
        program_master_id: programId,
        contactMethod: "",
        inquirerName: "",
        relationWithClient: "",
        phone1: "",
        phone2: "",
        email: "",
        address: "",
        howHeard: [],
        referredBy: { psychiatrist: "", psychologist: "", otherMedical: "" },
        otherSource: "",
        clientName: "",
        clientGender: "",
        clientDOB: "",
        client_cnic: "",
        client_phone: "",
        client_email: "",
        client_address: "",
        clientEducation: "",
        clientBirthOrder: "",
        clientSiblings: "",
        familyMentalIllness: "",
        familyMentalIllnessDetails: "",
        diagnosis: "",
        diagnosisSince: "",
        presentingComplaints: "",
        currentCare: "",
        trhServices: "",
        trhServicesDetails: "",
        substanceUse: "",
        substanceUseDetails: "",
        medications: [{ name: "", dosage: "" }],
        prescribedBy: "",
        prescriptionDate: "",
        prevServices: [],
        otherService: "",
        prevHospitalizations: [{ hospital: "", duration: "", consultant: "" }],
        medicalConditions: "",
        medicalConditionsDetails: "",
        healthIssues: [],
        otherHealthIssue: "",
        illnessHistory: "",
        expectations: "",
        suicidalThoughts: "",
        suicideFrequency: "",
        suicidePlans: "",
        suicideAttempts: "",
        suicideAttemptsDetails: "",
        desireEscape: "",
        escapeAttempts: "",
        escapeAttemptsDetails: "",
        historyAggression: "",
        historyAggressionDetails: "",
        harmToOthers: "",
        harmToOthersDetails: "",
        selfHarmHistory: "",
        selfHarmHistoryDetails: "",
        selfHarmUrges: "",
        selfHarmUrgesDetails: "",
        recommendedFor: [],
        opdDetails: "",
        followUpNotes: "",
        prev_aggression: "",
        last_episode: "",
        incident_provoked: "",
        regret_incident: "",
        show_denial: "",
        episode_description: "",
        cope_stress: "",
        client_general_behavior: "",
        impulsive_behavior: "",
        impulsive_example_1: "",
        impulsive_example_2: "",
        impulsive_example_3: "",
        symptoms: "",
        rehab_expectations: "",
        fear_aggression: "",
        psychiatrist_note: "",
        treatmentOption: "",
        reason: "",
        amount: "",
        payment_status: "",
        reference_no: "",
        payment_date: "",
    });

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="card">
                    <div className="card-body">
                        {/* Program Dropdown — Right Aligned */}
                        <div className="row mb-4 justify-content-end">
                            <div className="col-md-3">
                                <div className="form-floating form-floating-outline">
                                    <select
                                        className="form-select"
                                        value={selectedProgram}
                                        onChange={async (e) => {
                                            const newProgramId = e.target.value;

                                            if (newProgramId === selectedProgram) return;

                                            const isBetween1And3 = (selectedProgram === "1" && newProgramId === "3") || (selectedProgram === "3" && newProgramId === "1");

                                            if (isBetween1And3) {
                                                setSelectedProgram(newProgramId);
                                                setFormData(prev => ({
                                                    ...prev,
                                                    program_master_id: newProgramId
                                                }));
                                                return;
                                            }

                                            const result = await Swal.fire({
                                                title: "Change Program?",
                                                text: "Changing the program will clear all form data. Do you want to continue?",
                                                icon: "warning",
                                                showCancelButton: true,
                                                confirmButtonColor: "#3085d6",
                                                cancelButtonColor: "#d33",
                                                confirmButtonText: "Yes, change it",
                                                cancelButtonText: "Cancel",
                                                width: "340px",
                                                position: "top",
                                            });

                                            if (result.isConfirmed) {
                                                setSelectedProgram(newProgramId);
                                                setFormData(resetFormData(newProgramId));
                                                setActiveTab("enquirer-info");

                                                Swal.fire({
                                                    title: "Program Changed!",
                                                    text: "Please refill the form for the new program.",
                                                    icon: "info",
                                                    timer: 1500,
                                                    showConfirmButton: false,
                                                    position: "top",
                                                });
                                            }
                                        }}
                                    >
                                        {isProgramsLoading ? (
                                            <option>Loading programs...</option>
                                        ) : (
                                            programs.map((program) => (
                                                <option key={program.id} value={program.id}>
                                                    {program.name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                    <label>Select Program</label>
                                </div>
                            </div>
                        </div>

                        {/* Errors / Loader */}
                        {error && <div className="alert alert-danger">{error.toString()}</div>}
                        {isLoading && <div className="alert alert-info">Submitting...</div>}

                        <div className="bs-stepper wizard-vertical">
                            <div className="bs-stepper-header">
                                {tabs.map((tab) => (
                                    <div className="step" key={tab.key} data-target={`#${tab.key}`}>
                                        <button
                                            type="button"
                                            className={`step-trigger ${activeTab === tab.key ? "active" : ""}`}
                                            onClick={() => setActiveTab(tab.key)}
                                        >
                                            <span className="avatar">
                                                <span className="avatar-initial rounded-2 bg-primary text-white">
                                                    <i className={`${tab.icon} ri-24px`}></i>
                                                </span>
                                            </span>
                                            <span className="bs-stepper-label">
                                                <span className="bs-stepper-title">{tab.title}</span>
                                            </span>
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <div className="bs-stepper-content">
                                <form onSubmit={handleSubmit}>
                                    {activeTab === "enquirer-info" && (
                                        <EnquirerInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setActiveTab={setActiveTab}
                                            errors={errors}
                                            onNext={handleEnquirerNext}
                                        />
                                    )}
                                    {activeTab === "client-info" && (
                                        <ClientInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setActiveTab={setActiveTab}
                                            isAdd={false}
                                            errors={errors}
                                            onNext={handleClientNext}
                                        />
                                    )}
                                    {activeTab === "medications" && (
                                        <Medications
                                            formData={formData}
                                            handleChange={handleChange}
                                            addMedication={addMedication}
                                            removeMedication={removeMedication}
                                            addHospitalization={addHospitalization}
                                            removeHospitalization={removeHospitalization}
                                            setActiveTab={setActiveTab}
                                            selectedProgram={selectedProgram}
                                            errors={errors}
                                            onNext={handleMedicationsNext}
                                        />

                                    )}
                                    {["1", "3"].includes(selectedProgram) && activeTab === "physical-health" && (
                                        <PhysicalHealth
                                            formData={formData}
                                            handleChange={handleChange}
                                            setActiveTab={setActiveTab}
                                            errors={errors}
                                            onNext={handlePhysicalHealthNext}
                                        />
                                    )}

                                    {["1", "3"].includes(selectedProgram) && activeTab === "risk-assessment" && (
                                        <RiskAssessment
                                            formData={formData}
                                            handleChange={handleChange}
                                            setActiveTab={setActiveTab}
                                            errors={errors}
                                        />
                                    )}

                                    {!["1", "3"].includes(selectedProgram) && activeTab === "aggression-history" && (
                                        <AggressionHistory
                                            formData={formData}
                                            handleChange={handleChange}
                                            setActiveTab={setActiveTab}
                                            selectedProgram={selectedProgram}
                                            errors={errors}
                                            onNext={handleAggressionHistoryNext}
                                        />
                                    )}

                                    {!["1", "3"].includes(selectedProgram) && activeTab === "client-behavior" && (
                                        <ClientBehavior
                                            formData={formData}
                                            handleChange={handleChange}
                                            setActiveTab={setActiveTab}
                                            errors={errors}
                                            onNext={handleClientBehaviorNext}
                                        />
                                    )}
                                    {!["1", "3"].includes(selectedProgram) && activeTab === "treatment-plan" && (
                                        <TreatmentPlan
                                            formData={formData}
                                            handleChange={handleChange}
                                            setActiveTab={setActiveTab}
                                            errors={errors}
                                        />
                                    )}
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddInquiry;